import React from 'react';
import { TouchableOpacity, View } from 'react-native';

import { valueExists } from '../../../common/utils/utils';
import { getProductImageUrl } from '../../../common/utils/products/productImageUrlResolver';
import { ProductImage } from '../../components/image';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { SecondaryButton } from '../../design/components';
import Text from '../../components/text/Text';

import { formatQuantityWithPackaging, getStocktakeBatchSummaryText } from './StocktakeDetailHelpers';
import { createStocktakeDetailStyles } from './StocktakeDetail.styles';

const StocktakeItemCard = ({ item, quantity, onEdit, onZero, canEdit }) => {
	const styles = useThemedStyles(createStocktakeDetailStyles);
	const product = item.warehouseProduct?.product;
	const barcodes = product?.barcodes?.filter(Boolean) || [];
	const difference = valueExists(quantity)
		? (quantity - (item.preStocktakeQuantity || 0))
		: null;
	const preQuantityLabel = valueExists(item.preStocktakeQuantity)
		? formatQuantityWithPackaging(item.preStocktakeQuantity, item.warehouseProduct)
		: '—';
	const countedQuantityLabel = valueExists(quantity)
		? formatQuantityWithPackaging(quantity, item.warehouseProduct)
		: 'Not entered';
	const differenceLabel = valueExists(difference)
		? formatQuantityWithPackaging(difference, item.warehouseProduct)
		: '—';
	const expirySummary = getStocktakeBatchSummaryText(item);
	const imageUri = getProductImageUrl(product);

	return (
		<TouchableOpacity
			onPress={onEdit}
			activeOpacity={canEdit ? 0.85 : 1}
			accessibilityRole={canEdit ? 'button' : undefined}
			style={[
				styles.itemCard,
				valueExists(quantity) && styles.itemCardCompleted,
			]}
			disabled={!canEdit}
		>
			<View style={styles.itemHeaderRow}>
				<ProductImage
					uri={imageUri}
					style={styles.itemImage}
					placeholderStyle={styles.itemImagePlaceholder}
				/>
				<View style={styles.itemHeaderContent}>
					<Text style={styles.itemName}>
						{product?.name || 'Unnamed product'}
					</Text>
					<Text style={styles.itemBarcode}>
						Barcodes: {barcodes.length ? barcodes.join(', ') : '—'}
					</Text>
				</View>
			</View>
			<View style={styles.itemMetaRow}>
				<View style={[styles.itemMetaBlock, styles.itemMetaBlockSpacing]}>
					<Text style={styles.itemMetaLabel}>Pre</Text>
					<Text style={styles.itemMetaValue}>{preQuantityLabel}</Text>
				</View>
				<View style={styles.itemMetaBlock}>
					<Text style={styles.itemMetaLabel}>Counted</Text>
					<Text style={styles.itemMetaValue}>{countedQuantityLabel}</Text>
				</View>
			</View>
			<View style={[styles.itemMetaBlock, styles.itemMetaBlockFull]}>
				<Text style={styles.itemMetaLabel}>Difference</Text>
				<Text style={styles.itemMetaValue}>{differenceLabel}</Text>
			</View>
			<View style={[styles.itemMetaBlock, styles.itemMetaBlockFull]}>
				<Text style={styles.itemMetaLabel}>Expiry summary</Text>
				<Text style={styles.itemMetaValue}>{expirySummary}</Text>
			</View>
			{canEdit && (
				<Text style={styles.editHint}>
					Tap to edit batches
				</Text>
			)}
			{canEdit && onZero && (
				<SecondaryButton
					style={styles.zeroItemButton}
					label="Set count to zero"
					onPress={onZero}
				/>
			)}
		</TouchableOpacity>
	);
};

export default StocktakeItemCard;
